import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Card, Button } from '../../components';
import { reloadAndCheckVerified, resendVerificationEmail, refreshSession, routeAfterAuth, friendlyAuthError } from '../../services/auth-service';
import { auth } from '../../config/firebase';

const Verify = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialEmail = location.state?.email || auth.currentUser?.email || 'your email';
  const role = location.state?.role || 'founder';

  const [email] = useState<string>(initialEmail);
  const [isChecking, setIsChecking] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string>('');
  const [resentMsg, setResentMsg] = useState<string>('');
  const [resendCooldown, setResendCooldown] = useState(0);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Auto-poll: every 4 seconds reload the Firebase user — if they clicked the
  // verification link in another tab, we'll catch it without forcing them to
  // press the button.
  useEffect(() => {
    if (isVerified) return;
    let alive = true;
    const id = setInterval(async () => {
      try {
        const ok = await reloadAndCheckVerified();
        if (alive && ok) handleVerified();
      } catch { /* ignore */ }
    }, 4000);
    return () => { alive = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerified]);

  const handleVerified = async () => {
    setIsVerified(true);
    let destination = `/app/dashboard/${role}`;
    try {
      const session = await refreshSession();
      if (session) {
        destination = routeAfterAuth(session.user as any);
      }
    } catch {
      // Fall back to the role we have from location.state
    }
    setTimeout(() => navigate(destination), 1500);
  };

  const handleCheckNow = async () => {
    if (!auth.currentUser) {
      setError('Your session has expired — please sign in again.');
      setTimeout(() => navigate('/login'), 1500);
      return;
    }
    setIsChecking(true); setError('');
    try {
      const ok = await reloadAndCheckVerified();
      if (ok) {
        handleVerified();
      } else {
        setError("We don't see your email as verified yet — click the link in the email, then try again.");
      }
    } catch (err: any) {
      setError(friendlyAuthError(err) || 'Could not check verification — try again in a moment.');
    } finally {
      setIsChecking(false);
    }
  };

  const handleResend = async () => {
    if (!auth.currentUser) {
      setError('Your session has expired — please sign in again.');
      setTimeout(() => navigate('/login'), 1500);
      return;
    }
    setError(''); setResentMsg('');
    try {
      await resendVerificationEmail();
      setResentMsg('Verification email sent. Check your inbox (and spam).');
      setResendCooldown(45);
    } catch (err: any) {
      setError(friendlyAuthError(err) || 'Could not resend the verification email.');
    }
  };

  if (isVerified) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/logo.png" alt="BunyadKar" className="h-12 w-auto mx-auto" />
          </div>
          <Card>
            <div className="text-center">
              <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-success" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-2xl heading mb-4">Email Verified!</h2>
              <p className="text-muted mb-6">
                Your account has been successfully verified. Redirecting to your dashboard…
              </p>
              <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-text mb-2">BUNYADKAR</h1>
          <p className="text-muted">Forging Value Chains</p>
        </div>

        <Card>
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-full bg-chart/15 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-chart" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl heading mb-2">Check your inbox</h2>
            <p className="text-muted">
              We sent a verification link to <strong>{email}</strong>. Click it to confirm your account, then come back here.
            </p>
          </div>

          <Button
            onClick={handleCheckNow}
            variant="primary"
            size="lg"
            className="w-full"
            disabled={isChecking}
          >
            {isChecking ? 'Checking…' : "I've verified my email"}
          </Button>

          {error && (
            <div className="mt-3 text-center">
              <p className="text-danger text-sm">{error}</p>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0}
                className="text-xs text-chart hover:text-chart/80 font-medium underline mt-1 disabled:opacity-60 disabled:cursor-not-allowed disabled:no-underline"
              >
                {resendCooldown > 0 ? `Resend available in ${resendCooldown}s` : 'Resend the verification email'}
              </button>
            </div>
          )}
          {resentMsg && <p className="text-success text-sm mt-3 text-center">{resentMsg}</p>}

          <div className="mt-6 text-center space-y-3">
            <p className="text-sm text-muted">
              Didn't get the email? Check your spam folder.
            </p>
            <button
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="text-sm text-chart hover:text-chart/80 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend verification email'}
            </button>
            <p className="text-xs text-muted">
              Wrong email?{' '}
              <Link to="/register" className="text-chart hover:text-chart/80 font-medium">
                Start over
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Verify;
